<template>
  <v-form ref="rideTypeForm" v-model="valid">
    <v-layout style="width: 100%" row wrap>
      <v-flex xs12>
        <h4 class="grey--text text--darken-1">Ride Type</h4>
      </v-flex>
      <v-flex xs6 md2 px-2>
        <v-text-field
          :error-messages="errorForField('name')"
          :rules="rideTypeRules.name"
          v-model="rideType.name"
          label="Name"
          @input="resetError('name')"
        />
      </v-flex>
      <v-flex xs6 md4 px-2>
        <v-text-field
          :error-messages="errorForField('description')"
          :rules="rideTypeRules.description"
          v-model="rideType.description"
          label="Description"
          @input="resetError('description')"
        />
      </v-flex>
      <v-flex xs6 md3 px-2>
        <FileInput
          label="Image"
          icon="photo_camera"
          accept="image/*"
          :required="true"
          @fileInput="handleImageInput"
          :errorMessage="errorForField('image')"
          :rules="rideTypeRules.image"
        />
      </v-flex>
      <v-flex xs6 md3 px-2>
        <v-text-field
          :error-messages="errorForField('num_seats')"
          :rules="rideTypeRules.num_seats"
          v-model="rideType.num_seats"
          prepend-inner-icon="people"
          type="number"
          step="1"
          label="Number of Passengers"
          @input="resetError('num_seats')"
        />
      </v-flex>
      <v-flex xs6 md3 px-2>
        <v-text-field
          :error-messages="errorForField('availability_warning_threshold')"
          :rules="rideTypeRules.availability_warning_threshold"
          v-model="rideType.availability_warning_threshold"
          type="number"
          step="1"
          label="App Availability Warning Threshold (Minutes)"
          @input="resetError('availability_warning_threshold')"
        />
      </v-flex>
      <v-flex xs3 md2 pl-3 pr-2 pt-2>
        <v-checkbox v-model="rideType.disabled_access" color="primary" label="Disabled Access" />
      </v-flex>
      <v-flex xs3 md2 pl-3 pr-2 pt-2>
        <v-checkbox v-model="rideType.premium_vehicle" color="primary" label="Premium Vehicle" />
      </v-flex>
      <v-flex xs3 md2 pl-3 pr-2 pt-2>
        <v-switch v-model="rideType.enabled" color="primary" label=" Enabled" />
      </v-flex>
      <v-flex xs3 md3 pl-3 pr-2 pt-2>
        <v-switch
          v-model="rideType.requires_prebooking"
          color="primary"
          label=" Require Prebook (App)"
        />
      </v-flex>
    </v-layout>
    <v-layout style="width: 100%" row wrap pb-2>
      <v-flex xs12 md12 pb-1>
        <h4 class="grey--text text--darken-1">Tariff</h4>
      </v-flex>
      <v-flex xs3 md3 px-2>
        <v-text-field
          :error-messages="errorForField('base_fare')"
          :rules="tariffRules.base_fare"
          v-model="tariff.base_fare"
          prefix="£"
          type="number"
          class="noStepNumber"
          label="Base Fare"
          @input="resetError('base_fare')"
        />
      </v-flex>
      <v-flex xs3 md3 px-2>
        <v-text-field
          :error-messages="errorForField('per_mile_fare')"
          :rules="tariffRules.per_mile_fare"
          v-model="tariff.per_mile_fare"
          prefix="£"
          type="number"
          class="noStepNumber"
          label="Per Mile Fare"
          @input="resetError('per_mile_fare')"
        />
      </v-flex>
      <v-flex xs3 md3 px-2>
        <v-text-field
          :error-messages="errorForField('per_minute_fare')"
          :rules="tariffRules.per_minute_fare"
          v-model="tariff.per_minute_fare"
          prefix="£"
          type="number"
          class="noStepNumber"
          label="Per Minute Fare"
          @input="resetError('per_minute_fare')"
        />
      </v-flex>
      <v-flex xs3 md3 px-2>
        <v-text-field
          :error-messages="errorForField('additional_charge')"
          :rules="tariffRules.additional_charge"
          v-model="tariff.additional_charge"
          prefix="£"
          type="number"
          class="noStepNumber"
          label="Additional Charge"
          @input="resetError('additional_charge')"
        />
      </v-flex>
    </v-layout>
  </v-form>
</template>

<script>
import FileInput from "@/components/widget/input/FileInput.vue";

export default {
  name: "ride_type_form",
  components: {
    FileInput
  },
  props: {
    rideType: Object,
    tariff: Object,
    disabled: Boolean,
    errors: { type: Object, default: () => {} }
  },
  data: function() {
    return {
      valid: false,
      rideTypeRules: {
        name: [
          v => !!v || "Name is required",
          v => v.length <= 50 || "Name must be less than 200 characters"
        ],
        description: [
          v => !!v || "Description is required",
          v => v.length <= 200 || "Description must be less than 200 characters"
        ],
        image: [
          v => (this.rideType.picture ? true : !!v || "Image is required")
        ],
        num_seats: [
          v => !!v || "Number of passengers is required",
          v => (v > 0 && v < 9) || "No. of passengers must be between 1 and 8"
        ],
        availability_threshold: [
          v => !!v || "Availability Threshold is required",
          v =>
            v >= 0 ||
            "Availability threshold must be a positive number of minutes"
        ]
      },
      tariffRules: {
        base_fare: [
          v => !!v || v == 0 || "Base fare is required",
          v => /^\d+\.?\d?\d?$/.test(v) || "Please enter a valid amount"
        ],
        per_mile_fare: [
          v => !!v || v == 0 || "Per mile fare is required",
          v => /^\d+\.?\d?\d?$/.test(v) || "Please enter a valid amount"
        ],
        per_minute_fare: [
          v => !!v || v == 0 || "Per minute fare is required",
          v => /^\d+\.?\d?\d?$/.test(v) || "Please enter a valid amount"
        ],
        additional_charge: [
          v => !!v || v == 0 || "Additional charge is required",
          v => /^\d+\.?\d?\d?$/.test(v) || "Please enter a valid amount"
        ]
      }
    };
  },
  methods: {
    errorForField: function(field) {
      try {
        return this.errors[field];
      } catch (err) {
        return null;
      }
    },
    resetError: function(field) {
      try {
        if (this.errors[field] != "") {
          this.errors[field] = "";
        }
      } catch (err) {}
    },
    validateForm: function() {
      this.$refs.rideTypeForm.validate();
    },
    handleImageInput: function(file) {
      this.resetError("image");
      this.$emit("imageInput", file);
    }
  },
  watch: {
    valid: function() {
      this.$emit("validityChange", this.valid);
    }
  }
};
</script>

<style>
.noStepNumber input[type="number"] {
  -moz-appearance: textfield;
}
.noStepNumber input::-webkit-outer-spin-button,
.noStepNumber input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
</style>